import React, { useState, useEffect, useRef } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import '../shared.css';
import LoadingOverlay from '../LoadingOverlay';

function DailySalesPaymentWise() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const [totalcgst, setTotalcgst] = useState(0);
    const [totalsgst, setTotalsgst] = useState(0);
    const [totaligst, setTotaligst] = useState(0);
    const [totalnet, setTotalnet] = useState(0);
    const [totalamt, setTotalamt] = useState(0);
    const [totalpurchase, setTotalpurchase] = useState(0);
    const [totalqty, setTotalqty] = useState(0);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        const startDate = getFormattedStartDate();
        const currentDate = getFormattedCurrentDate();
        setFromDate(startDate);
        setToDate(currentDate);
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        if (fromDate && toDate) {
            fetchTableData(fromDate, toDate);
        }
    }, [fromDate, toDate]);

    const [totalbills, setTotalbills] = useState(0);

    useEffect(() => {
        let totalcgst = 0, totalsgst = 0, totaligst = 0, totalnet = 0, totalamt = 0, totalpurchase = 0, qty = 0;
        let billNumbers = new Set();
        dataItem.forEach(item => {
            totalcgst += item.CGSTAmount;
            totalsgst += item.SGSTAmount;
            totaligst += item.IGSTAmount;
            totalnet += item.TaxableAmount;
            totalamt += item.TotalAmount;
            totalpurchase += item.PurchaseAmount;
            qty += item.Quantity;
            billNumbers.add(item.BillNumber);
        });
        setTotalcgst(totalcgst);
        setTotalsgst(totalsgst);
        setTotaligst(totaligst);
        setTotalnet(totalnet);
        setTotalamt(totalamt);
        setTotalqty(qty);
        setTotalpurchase(totalpurchase);
        setTotalbills(billNumbers.size);
    }, [dataItem]);

    const fetchCustomerData = () => {};

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];

    const getFormattedStartDate = () => {
        const d = new Date();
        d.setDate(1);
        return d.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => setFromDate(e.target.value);
    const handleToDateChange = (e) => setToDate(e.target.value);

    const hasReadReportPermission = permissions.includes('Read Reports');
    const [loading, setLoading] = useState(false);
    const loadingRef = useRef(false);

    const fetchTableData = async (fDate, tDate) => {
        const effectiveFromDate = fDate || fromDate;
        const effectiveToDate = tDate || toDate;
        if (hasReadReportPermission && !loadingRef.current && effectiveFromDate && effectiveToDate) {
            setDataItem([]);
            loadingRef.current = true;
            setLoading(true);
            try {
                const response = await fetchData('ReportApi', `/DailySalesSummaryPayment/${companyId}/${branchId}/${effectiveFromDate}/${effectiveToDate}`, token);
                if (response.responseCode === '1111') {
                    setDataItem(response.response_data);
                }
            } catch (error) {
                console.error('Error fetching Daily Sales Summary Details:', error);
            } finally {
                loadingRef.current = false;
                setLoading(false);
            }
        }
    };

    const handleSearch = (event) => setSearchQuery(event.target.value);

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('DSS Payment Wise');

        const rightAlignColumns = [
            'Quantity', 'MRP', 'MRP On Net', 'Discount %', 'Discount value',
            'Additional Disc%', 'Add Disc Value', 'Cash Discount', 'Promo Disc%', 'Promo Disc Value', 'Balance',
            'GST %', 'CGST Amount', 'SGST Amount', 'IGST Amount', 'Net Value',
            'Gift Voucher', 'Net Value + Tax', 'Purchase Rate', 'Total Purchase Value',
            'Cash Amount', 'Card Amount', 'UPI/RTGS/Other Amount'
        ];

        worksheet.mergeCells('A1:AW1');
        const captionCell = worksheet.getCell('A1');
        captionCell.value = `Daily Sales Summary Payment Wise From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`;
        captionCell.font = { bold: true, size: 14 };
        captionCell.alignment = { vertical: 'middle', horizontal: 'left' };

        const headerRowValues = [
            'Date', 'Time', 'Memo No', 'PG', 'IG', 'ISG', 'PF', 'Item No', 'Description', 'HSN Code', 'Quantity', 'MRP', 'MRP On Net',
            'Discount %', 'Discount value', 'Additional Disc%', 'Add Disc Value', 'Cash Discount', 'Promo Disc%', 'Promo Disc Value', 'Balance', 'GST %', 'CGST Amount',
            'SGST Amount', 'IGST Amount', 'Net Value', 'Gift Voucher', 'Net Value + Tax',
            'Mode Of Payment', 'Cash Amount', 'Card Amount', 'UPI/RTGS/Other Amount', 'Card Type', 'Card Number',
            'UPI/RTGS/Other Number', 'Discount Type', 'SC Membership Number', 'Voucher Number', 'GST Number', 'Sales Man',
            'Comments', 'GWP/PWP', 'Inter-City Stock Transfer', 'Category', 'Group', 'Season Code', 'Size Code',
            'Customer Name', 'Contact Number', 'Customer Address', 'Exclusion'
        ];
        const thinBorder = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB6D7A8' } };
            cell.font = { bold: true, color: { argb: 'FF000000' } };
            cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
            cell.border = thinBorder;
        });

        filteredDataItem.forEach(rowData => {
            const rowValues = [
                formatDate(rowData.BillDate), rowData.BillTime, rowData.BillNumber, rowData.PG, rowData.IG, rowData.ISG, rowData.PF, parseFloat(rowData.Code),
                rowData.ItemName, rowData.HSNCode, rowData.Quantity, rowData.Rate, rowData.Amount, rowData.DiscountPercentage, rowData.DiscountAmount,
                rowData.AdditionalDiscountPercentage, rowData.AdditionalDiscountAmount, rowData.CashDiscount,
                parseFloat(rowData.PromotionalDiscountPercentage.toFixed(2)), parseFloat(rowData.PromotionalDiscountAmount.toFixed(2)),
                rowData.TotalAmount, rowData.GSTRate, rowData.CGSTAmount, rowData.SGSTAmount, rowData.IGSTAmount, rowData.TaxableAmount, '', rowData.TotalAmount,
                rowData.ModeOfPayment, rowData.CashAmount, rowData.CardAmount, rowData.UPIAmount, rowData.CardType, rowData.CardNumber,
                rowData.UPINumber, rowData.DiscType, rowData.SCMembershipNumber, rowData.VoucherNumber, rowData.GSTNumber, rowData.SalesManName,
                rowData.Remarks, rowData.Gwp, rowData.InterStockTransfer, rowData.CategoryName, rowData.ItemGroupName, rowData.SeasonCode, rowData.SizeCode,
                rowData.CustomerName, rowData.ContactNumber, rowData.CustomerAddress, rowData.IsExcludingItem
            ];
            const row = worksheet.addRow(rowValues);
            row.eachCell((cell, colNumber) => {
                const headerName = headerRowValues[colNumber - 1];
                if (rightAlignColumns.includes(headerName)) {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                    cell.numFmt = '#,##0.00';
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                }
                cell.border = thinBorder;
            });
        });

        const totalRowValues = [
            '', '', '', '', '', '', '', '', 'Total Bills', totalbills, totalqty, '', '', '', '', '', '', '', '', '',
            totalamt, '', totalcgst, totalsgst, totaligst, totalnet, '', totalamt,
            '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''
        ];
        const totalRow = worksheet.addRow(totalRowValues);
        totalRow.font = { bold: true };
        totalRow.eachCell((cell, colNumber) => {
            const headerName = headerRowValues[colNumber - 1];
            if (rightAlignColumns.includes(headerName)) {
                cell.alignment = { vertical: 'middle', horizontal: 'right' };
                cell.numFmt = '#,##0.00';
            } else {
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
            }
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2EFDA' } };
            cell.border = thinBorder;
        });

        worksheet.columns = headerRowValues.map(() => ({ width: 15 }));
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'DSS_PaymentWise.xlsx');
    };

    return (
        <div className="pg-container">
            <div className="pg-header">
                <h5 className="pg-title">Daily Sales Summary Payment Wise</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input className="pg-input" type="date" value={fromDate} onChange={handleFromDateChange} />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input className="pg-input" type="date" value={toDate} onChange={handleToDateChange} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={() => fetchTableData()} disabled={loading}>
                            <RefreshIcon /> {loading ? 'Loading...' : 'Refresh'}
                        </button>
                        <button className="pg-btn pg-btn-success" onClick={handleExportExcel}>
                            <FeedIcon /> Excel
                        </button>
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearchInput(v => !v)}>
                            <SearchIcon /> Search
                        </button>
                    </div>
                    {showSearchInput && (
                        <input className="pg-input pg-search-input" placeholder="Search…"
                            value={searchQuery} onChange={handleSearch} autoFocus style={{ marginTop: 6 }} />
                    )}
                </div>
            </div>

            {loading && <LoadingOverlay />}

            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Memo No</th>
                            <th>PG</th>
                            <th>IG</th>
                            <th>ISG</th>
                            <th>PF</th>
                            <th>Item No</th>
                            <th>Description</th>
                            <th>HSN Code</th>
                            <th>Quantity</th>
                            <th>MRP</th>
                            <th>MRP On Net</th>
                            <th>Discount %</th>
                            <th>Discount value</th>
                            <th>Additional Disc%</th>
                            <th>Add Disc Value</th>
                            <th>Cash Discount</th>
                            <th>PROMO Disc%</th>
                            <th>PROMO Disc Value</th>
                            <th>Balance</th>
                            <th>GST %</th>
                            <th>CGST Amount</th>
                            <th>SGST Amount</th>
                            <th>IGST Amount</th>
                            <th>Net Value</th>
                            <th>Gift Voucher</th>
                            <th>Net Value + Tax</th>
                            <th>Mode Of Payment</th>
                            <th>Cash Amount</th>
                            <th>Card Amount</th>
                            <th>UPI/RTGS/Other Amount</th>
                            <th>Card Type</th>
                            <th>Card Number</th>
                            <th>UPI/RTGS/Other Number</th>
                            <th>Discount Type</th>
                            <th>SC Membership Number</th>
                            <th>Voucher Number</th>
                            <th>GST Number</th>
                            <th>Sales Man</th>
                            <th>Comments</th>
                            <th>GWP/PWP</th>
                            <th>Inter-City Stock Transfer</th>
                            <th>Category</th>
                            <th>Group</th>
                            <th>Season Code</th>
                            <th>Size Code</th>
                            <th>Customer Name</th>
                            <th>Contact Number</th>
                            <th>Customer Address</th>
                            <th>Exclusion</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDataItem.map((data, index) => (
                            <tr key={index}>
                                <td>{formatDate(data.BillDate)} {data.BillTime}</td>
                                <td>{data.BillNumber}</td>
                                <td>{data.PG}</td>
                                <td>{data.IG}</td>
                                <td>{data.ISG}</td>
                                <td>{data.PF}</td>
                                <td>{data.Code}</td>
                                <td>{data.ItemName}</td>
                                <td>{data.HSNCode}</td>
                                <td>{data.Quantity}</td>
                                <td>{data.Rate.toFixed(2)}</td>
                                <td>{data.Amount.toFixed(2)}</td>
                                <td>{data.DiscountPercentage}</td>
                                <td>{data.DiscountAmount.toFixed(2)}</td>
                                <td>{data.AdditionalDiscountPercentage}</td>
                                <td>{data.AdditionalDiscountAmount.toFixed(2)}</td>
                                <td>{data.CashDiscount.toFixed(2)}</td>
                                <td>{data.PromotionalDiscountPercentage.toFixed(2)}</td>
                                <td>{data.PromotionalDiscountAmount.toFixed(2)}</td>
                                <td>{data.TotalAmount.toFixed(2)}</td>
                                <td>{data.GSTRate}</td>
                                <td>{data.CGSTAmount.toFixed(2)}</td>
                                <td>{data.SGSTAmount.toFixed(2)}</td>
                                <td>{data.IGSTAmount.toFixed(2)}</td>
                                <td>{data.TaxableAmount.toFixed(2)}</td>
                                <td></td>
                                <td>{data.TotalAmount.toFixed(2)}</td>
                                <td>{data.ModeOfPayment}</td>
                                <td>{data.CashAmount.toFixed(2)}</td>
                                <td>{data.CardAmount.toFixed(2)}</td>
                                <td>{data.UPIAmount.toFixed(2)}</td>
                                <td>{data.CardType}</td>
                                <td>{data.CardNumber}</td>
                                <td>{data.UPINumber}</td>
                                <td>{data.DiscType}</td>
                                <td>{data.SCMembershipNumber}</td>
                                <td>{data.VoucherNumber}</td>
                                <td>{data.GSTNumber}</td>
                                <td>{data.SalesManName}</td>
                                <td>{data.Remarks}</td>
                                <td>{data.Gwp}</td>
                                <td>{data.InterStockTransfer}</td>
                                <td>{data.CategoryName}</td>
                                <td>{data.ItemGroupName}</td>
                                <td>{data.SeasonCode}</td>
                                <td>{data.SizeCode}</td>
                                <td>{data.CustomerName}</td>
                                <td>{data.ContactNumber}</td>
                                <td>{data.CustomerAddress}</td>
                                <td>{data.IsExcludingItem}</td>
                            </tr>
                        ))}
                        <tr style={{ background: '#1c2a3a', color: '#fff', fontWeight: 700 }}>
                            <td colSpan={7}></td>
                            <td>Total Bills</td>
                            <td>{totalbills}</td>
                            <td>{totalqty}</td>
                            <td colSpan={9}></td>
                            <td>{totalamt.toFixed(2)}</td>
                            <td></td>
                            <td>{totalcgst.toFixed(2)}</td>
                            <td>{totalsgst.toFixed(2)}</td>
                            <td>{totalnet.toFixed(2)}</td>
                            <td></td>
                            <td></td>
                            <td>{totalamt.toFixed(2)}</td>
                            <td colSpan={24}></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default DailySalesPaymentWise;
